import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SwapVert, ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { TableHeader } from '../types';
import { RootState } from '../../../../../../redux/root';
import { setTableSortingDirection, setTableSortingKey } from '../../../../../../redux/slices/generalSlice';
import CellFilter from './CellFilter';

const TableHeaderCell: FC<TableHeader> = ({ label, sortingKey, hideFilter, itemName, showFilters, customFilter }) => {
  const [selected, setSelected] = useState(0);
  const [active, setActive] = useState(false);
  const { tableSortingKey } = useSelector((state: RootState) => state.general);
  const dispatch = useDispatch();

  useEffect(() => {
    setActive(sortingKey === tableSortingKey);
  }, [tableSortingKey]);

  useEffect(() => {
    dispatch(setTableSortingDirection(getSortingValue()));
  }, [selected]);

  const getSortingValue = () => {
    if (selected % 3 === 0) return 'NONE';
    return selected % 3 === 1 ? 'ASC' : 'DESC';
  };

  const isSortable = sortingKey !== undefined;
  const showAsActive = active && selected % 3 !== 0;

  return (
    <div>
      <HeaderCell
        as={isSortable ? 'button' : 'div'}
        $sortable={isSortable}
        onClick={
          isSortable
            ? () => {
                setSelected(selected + 1);
                dispatch(setTableSortingKey(sortingKey));
              }
            : undefined
        }
        type={isSortable ? 'button' : undefined}
      >
        <HeaderLabel $active={showAsActive}>{label}</HeaderLabel>
        {isSortable && (
          <SortingIcon $active={showAsActive}>
            {showAsActive ? (
              <>
                {selected % 3 === 1 && <ArrowUpward />}
                {selected % 3 === 2 && <ArrowDownward />}
              </>
            ) : (
              <SwapVert />
            )}
          </SortingIcon>
        )}
      </HeaderCell>
      {showFilters && !hideFilter && (customFilter ?? <CellFilter name={itemName} />)}
      {showFilters && hideFilter && <FilterSpacer />}
    </div>
  );
};

export default TableHeaderCell;

const HeaderCell = styled.div<{ $sortable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 16px;
  margin: 0;
  background: none;
  border: 0;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  text-align: left;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};

  &:focus-visible {
    outline: none;
    box-shadow: ${({ $sortable }) => ($sortable ? 'var(--shadow-focus)' : 'none')};
    border-radius: 6px;
  }
`;

const HeaderLabel = styled.span<{ $active: boolean }>`
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--neutral-500)')};
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SortingIcon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--neutral-400)')};
  transition: color 0.12s ease;

  svg {
    font-size: 16px;
  }

  ${HeaderCell}:hover & {
    color: var(--primary-600);
  }
`;

const FilterSpacer = styled.div`
  width: 100%;
  height: 42px;
`;
